/**
 * File Upload Component
 * Handles audio file selection and upload progress display
 */

import React, { useCallback, useRef, useState } from 'react';
import { Card, Button, Stack, Flex, useTheme, spacing, fontSize } from '@fti/design-system';

export interface FileUploadProps {
  onFileSelect: (file: File) => void;
  accept?: string;
  maxSizeMB?: number;
  disabled?: boolean;
  uploadProgress?: number;
  isUploading?: boolean;
}

/**
 * File upload component with drag-and-drop support
 */
export function FileUpload({
  onFileSelect,
  accept = '.mp3,audio/mpeg',
  maxSizeMB = 500,
  disabled = false,
  uploadProgress = 0,
  isUploading = false,
}: FileUploadProps) {
  const { theme } = useTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const textColor = theme.colors.text.primary;
  const mutedColor = theme.colors.text.secondary;
  const errorColor = theme.colors.semantic.error.default;
  const successColor = theme.colors.semantic.success.default;
  const primaryColor = theme.colors.primary[600];

  const validateFile = useCallback((file: File): string | null => {
    // Check file type
    const validTypes = ['audio/mpeg', 'audio/mp3'];
    if (!validTypes.includes(file.type) && !file.name.toLowerCase().endsWith('.mp3')) {
      return 'Please select an MP3 audio file';
    }

    // Check file size
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxSizeBytes) {
      return `File size must be less than ${maxSizeMB}MB`;
    }

    return null;
  }, [maxSizeMB]);

  const handleFile = useCallback((file: File) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      setSelectedFile(null);
      return;
    }

    setError(null);
    setSelectedFile(file);
    onFileSelect(file);
  }, [validateFile, onFileSelect]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFile(file);
    }
  }, [handleFile]);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragging(true);
    }
  }, [disabled]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled) return;

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFile(file);
    }
  }, [disabled, handleFile]);

  const handleButtonClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleClear = useCallback(() => {
    setSelectedFile(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <Stack gap="md">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleInputChange}
        disabled={disabled || isUploading}
        style={{ display: 'none' }}
      />

      {/* Drop zone */}
      <div
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        style={{
          border: `2px dashed ${isDragging ? primaryColor : theme.colors.border.default}`,
          borderRadius: '8px',
          padding: spacing.lg,
          textAlign: 'center',
          background: isDragging ? `${primaryColor}10` : theme.colors.background.paper,
          cursor: disabled || isUploading ? 'not-allowed' : 'pointer',
          opacity: disabled || isUploading ? 0.6 : 1,
          transition: 'all 0.2s ease',
        }}
        onClick={disabled || isUploading ? undefined : handleButtonClick}
      >
        {!selectedFile ? (
          <Stack gap="sm" align="center">
            <span style={{ fontSize: fontSize['2xl'] }}>📁</span>
            <p style={{ margin: 0, color: textColor, fontWeight: 500 }}>
              {isDragging ? 'Drop file here' : 'Drag and drop an MP3 file'}
            </p>
            <p style={{ margin: 0, color: mutedColor, fontSize: fontSize.sm }}>
              or click to browse
            </p>
            <p style={{ margin: 0, color: mutedColor, fontSize: fontSize.xs }}>
              Maximum file size: {maxSizeMB}MB
            </p>
          </Stack>
        ) : (
          <Stack gap="sm" align="center">
            <span style={{ fontSize: fontSize['2xl'] }}>🎵</span>
            <p style={{ margin: 0, color: textColor, fontWeight: 500 }}>
              {selectedFile.name}
            </p>
            <p style={{ margin: 0, color: mutedColor, fontSize: fontSize.sm }}>
              {formatFileSize(selectedFile.size)}
            </p>
          </Stack>
        )}
      </div>

      {/* Error message */}
      {error && (
        <div
          style={{
            padding: spacing.sm,
            background: `${errorColor}15`,
            border: `1px solid ${errorColor}`,
            borderRadius: '6px',
            color: errorColor,
            fontSize: fontSize.sm,
          }}
        >
          {error}
        </div>
      )}

      {/* Upload progress */}
      {isUploading && (
        <Stack gap="xs">
          <Flex justify="between" align="center">
            <span style={{ fontSize: fontSize.sm, color: textColor }}>
              Uploading...
            </span>
            <span style={{ fontSize: fontSize.sm, color: mutedColor }}>
              {Math.round(uploadProgress)}%
            </span>
          </Flex>
          <div
            style={{
              height: '8px',
              background: theme.colors.background.paper,
              borderRadius: '4px',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: '100%',
                width: `${uploadProgress}%`,
                background: primaryColor,
                transition: 'width 0.3s ease',
              }}
            />
          </div>
        </Stack>
      )}

      {/* Action buttons */}
      {selectedFile && !isUploading && (
        <Flex gap="sm">
          <Button
            variant="outline"
            color="error"
            size="sm"
            onClick={handleClear}
          >
            Clear
          </Button>
          <Button
            variant="outline"
            color="primary"
            size="sm"
            onClick={handleButtonClick}
          >
            Change File
          </Button>
        </Flex>
      )}
    </Stack>
  );
}

export default FileUpload;
